import React, { useId, useState } from 'react';
import Autocomplete from '@mui/material/Autocomplete';
import TextField from '@mui/material/TextField';
import { Checkbox, CircularProgress } from '@mui/material';
import CheckBoxOutlineBlankIcon from '@mui/icons-material/CheckBoxOutlineBlank';
import CheckBoxIcon from '@mui/icons-material/CheckBox';
import { useQuery } from '@tanstack/react-query';
import queryKeyAppInstance from 'redux/slices/appInstance/constants/queryKeyAppInstance';
import { getListAppInstanceQueryWithAssocicated } from 'redux/slices/appInstance/apis';
import {
    DEFAULT_PAGE_INDEX,
    DEFAULT_PAGE_SIZE,
} from '@tenant/utility/constants/default';
import { debounce } from '@tenant/utility/helper/Utils';
const icon = <CheckBoxOutlineBlankIcon fontSize='small' />;
const checkedIcon = <CheckBoxIcon fontSize='small' />;

const FIELD_NAME = 'AppInstanceAssociatedList';
const KEY = 'Name';
const LABEL = 'Name';

const DockerImageSelect = (props) => {
    const {
        error,
        helperText,
        value = [],
        required,
        handleInputChange,
        excludes = [],
        textFieldProps,
        isMultiple = true,
        label,
        queryKey,
        params = {},
        ...rest
    } = props;
    const [searchTerm, setSearchTerm] = useState('');
    const [isEnabled, setIsEnabled] = useState(false);

    const { data, isLoading, isRefetching } = useQuery({
        enabled: isEnabled,
        queryKey: queryKeyAppInstance.getList('select-' + searchTerm + queryKey),
        queryFn: () =>
            getListAppInstanceQueryWithAssocicated({
                pageIndex: DEFAULT_PAGE_INDEX,
                pageSize: DEFAULT_PAGE_SIZE,
                searchTerm,
                ...params,
            }),
    });

    const id = useId();

    const onInputChange = debounce(async (e, value) => {
        if (e?.type === 'change') {
            setSearchTerm(value);
        }
    }, 500);

    const onOpen = () => {
        setIsEnabled(true);
    };

    return (
        <Autocomplete
            fullWidth
            multiple={isMultiple}
            disableCloseOnSelect={isMultiple}
            value={value}
            id={`${FIELD_NAME}-select-${id}`}
            name={FIELD_NAME}
            options={
                data?.data?.Items?.filter(
                    (e) => !excludes?.some((f) => f[KEY] === e[KEY]),
                ) ?? []
            }
            onInputChange={onInputChange}
            onChange={(e, value) => {
                setSearchTerm('');
                rest?.onChange?.(e, value);
            }}
            onOpen={onOpen}
            getOptionLabel={(option) => option[LABEL]}
            isOptionEqualToValue={(option, value) => {
                return option?.[KEY] === value?.[KEY];
            }}
            renderOption={(props, option, { selected }) => {
                const { key, ...optionProps } = props;
                return (
                    <li {...optionProps} key={option?.[KEY]}>
                        {isMultiple && (
                            <Checkbox
                                icon={icon}
                                checkedIcon={checkedIcon}
                                style={{ marginRight: 8 }}
                                checked={selected}
                            />
                        )}
                        {option[LABEL]}
                    </li>
                );
            }}
            loading={isLoading || isRefetching}
            renderInput={(params) => (
                <TextField
                    {...params}
                    label={label}
                    required={required}
                    error={error}
                    helperText={helperText}
                    placeholder={
                        isMultiple ? 'Select App Instances' : 'Select App Instance'
                    }
                    InputProps={{
                        ...params.InputProps,
                        endAdornment: (
                            <React.Fragment>
                                {isLoading ? (
                                    <CircularProgress color='inherit' size={20} />
                                ) : null}
                                {params.InputProps.endAdornment}
                            </React.Fragment>
                        ),
                    }}
                    {...textFieldProps}
                />
            )}
            {...rest}
        />
    );
};

export default React.memo(DockerImageSelect);
